/** Which character is speaking, and which is still loading. */
export type PreviewState = {
  playing: string | null;
  loading: string | null;
};

export type PreviewAction =
  { type: 'play'; id: string } | { type: 'ready'; id: string } | { type: 'failed'; id: string } | { type: 'ended' };

/**
 * One voice at a time.
 *
 * The rule that earns the reducer is the last two cases: a `ready` or a
 * `failed` for anything but the character currently loading is a late arrival
 * from a tap the learner has already moved on from. Playing that audio would
 * be a voice with no visible cause, and clearing the spinner on it would leave
 * the current one looking finished when it is not.
 */
export function previewReducer(state: PreviewState, action: PreviewAction): PreviewState {
  switch (action.type) {
    case 'play':
      // Tapping the one that is speaking means stop, not start again.
      if (state.playing === action.id) return { playing: null, loading: null };
      return { playing: null, loading: action.id };
    case 'ready':
      if (state.loading !== action.id) return state;
      return { playing: action.id, loading: null };
    case 'failed':
      if (state.loading !== action.id) return state;
      return { playing: null, loading: null };
    case 'ended':
      return { ...state, playing: null };
  }
}

/**
 * Which expression a character's face wears on the select screen.
 *
 * The card whose voice is playing speaks; every other card is at rest. A
 * loading card stays at rest on purpose — the spinner on its Hear button
 * already says something is happening, and a mouth moving before any sound
 * arrives would be the face claiming to talk while silent.
 */
export function expressionForCard(
  state: PreviewState,
  id: string,
  celebrating: string | null = null,
): 'neutral' | 'speaking' | 'celebrate' {
  if (celebrating === id) return 'celebrate';
  return state.playing === id ? 'speaking' : 'neutral';
}
